import { Singleflight, SmartFrameDropper } from "./util";

const TARGET_FRAMERATE = 30;

let videoTrack = null;
let videoDescription = null;
let decodeQueue = [];
let decoder = null;
let encoder = null;
let decodeError = false;
let encodeError = false;
let encoderConfig = null;
let targetWidth = 1920;
let targetHeight = 1080;
const encodedChunks = [];

const singleflight = Singleflight.getInstance();
let processFrameCount = 0;

/**
 * 根据分辨率获取推荐的码率
 * @param {*} width
 * @param {*} height
 * @returns
 */
function getRecommendedBitrate(width, height) {
  const p = width * height;
  if (p <= 320 * 240) return 300_000; // 240p
  if (p <= 640 * 480) return 600_000; // 480p
  if (p <= 1280 * 720) return 1_500_000; // 720p
  if (p <= 1920 * 1080) return 2_000_000; // 1080p
  return 2_000_000; // 4K
}

/**
 * 获取在指定范围的目标分辨率
 * @param {*} videoTrack
 * @param {*} maxWidth
 * @param {*} maxHeight
 */
function getVideoTargetResolution(videoTrack, maxLong, maxShort) {
  const videoRatio = videoTrack.track_width / videoTrack.track_height;
  let targetWidth = Math.round(videoTrack.track_width);
  let targetHeight = Math.round(videoTrack.track_height);
  if (videoRatio >= 1) {
    // 横屏
    if (videoRatio >= maxLong / maxShort) {
      if (targetWidth > maxLong) {
        targetWidth = maxLong;
        targetHeight = Math.floor(targetWidth / videoRatio);
      }
    } else {
      if (targetHeight > maxShort) {
        targetHeight = maxShort;
        targetWidth = Math.floor(targetHeight * videoRatio);
      }
    }
  } else {
    // 竖屏
    if (videoRatio <= maxShort / maxLong) {
      if (targetHeight > maxLong) {
        targetHeight = maxLong;
        targetWidth = Math.floor(targetHeight * videoRatio);
      }
    } else {
      if (targetWidth > maxShort) {
        targetWidth = maxShort;
        targetHeight = Math.floor(targetWidth / videoRatio);
      }
    }
  }
  return {
    width: targetWidth,
    height: targetHeight,
  };
}

/**
 * 初始化 webcodec
 * @param {*} videoTrack
 * @returns
 */
const prepareWebCodec = (videoTrack, videoDescription) => {
  return singleflight.do("prepare_webcodec", async () => {
    if (decoder && encoder) {
      return;
    }
    try {
      // 配置解码器
      const decoderConfig = {
        codec: videoTrack.codec,
        codedWidth: videoTrack.track_width,
        codedHeight: videoTrack.track_height,
        description: videoDescription,
      };
      console.log("解码器配置:", decoderConfig);
      // 检查解码器支持
      const isDecoderSupported =
        await VideoDecoder.isConfigSupported(decoderConfig);
      if (!isDecoderSupported.supported) {
        throw new Error("不支持的解码器配置: " + JSON.stringify(decoderConfig));
      }
      decoder = new VideoDecoder({
        output: (frame) => {
          // 解码后的帧会在这里处理
          processFrame(frame, encoder).finally(() => {
            frame.close();
          });
        },
        error: (e) => {
          console.error("解码器错误", e);
          decodeError = e;
        },
      });
      decoder.configure(decoderConfig);

      // 步骤3: 配置编码器
      const originalFramerate =
        videoTrack.nb_samples / (videoTrack.duration / videoTrack.timescale);
      const res = getVideoTargetResolution(videoTrack, 1920, 1080);
      targetWidth = res.width;
      targetHeight = res.height;
      encoderConfig = {
        codec: "avc1.640028", // H.264 High Profile
        width: targetWidth,
        height: targetHeight,
        framerate: Math.min(Math.ceil(originalFramerate), TARGET_FRAMERATE), // 限制最大帧率为30fps
        bitrate: Math.min(
          videoTrack.bitrate,
          getRecommendedBitrate(targetWidth, targetHeight),
        ), // 动态比特率
        keyFrameEvery: 60, // 每60帧一个关键帧，减少文件大小
      };
      // 检查编码器支持
      let isSupported = await VideoEncoder.isConfigSupported(encoderConfig);
      if (!isSupported.supported) {
        encoderConfig.codec = "avc1.4d0028";
        isSupported = await VideoEncoder.isConfigSupported(encoderConfig);
        if (!isSupported.supported) {
          // 42(Baseline)其实也支持 1080p，但是如果当前设备不支持 46(Main)，大概率配置也不会很高，所以降级到 720p，减少设备压力
          encoderConfig.codec = "avc1.42001f";
          const res1 = getVideoTargetResolution(videoTrack, 1280, 720);
          targetWidth = res1.width;
          targetHeight = res1.height;
          encoderConfig.width = targetWidth;
          encoderConfig.height = targetHeight;
          isSupported = await VideoEncoder.isConfigSupported(encoderConfig);
          if (!isSupported.supported) {
            throw new Error(
              "不支持的编码器配置: " +
                JSON.stringify(encoderConfig) +
                "，详情: " +
                JSON.stringify(isSupported),
            );
          }
        }
      }
      console.log("编码器配置:", encoderConfig);
      encoder = new VideoEncoder({
        output: (chunk, metadata) => {
          // 为每个 chunk 提供基本的 metadata，避免 "metadata must be provided" 错误
          const chunkMetadata = metadata || {
            decoderConfig: {
              codec: encoderConfig.codec,
              codedWidth: encoderConfig.width,
              codedHeight: encoderConfig.height,
              colorSpace: {
                fullRange: false,
                matrix: "bt709",
                primaries: "bt709",
                transfer: "iec61966-2-1",
              },
              description: null,
            },
          };

          // 第一个 chunk 的详细信息
          if (encodedChunks.length === 0) {
            console.log("第一个编码块的 metadata:", chunkMetadata);
          }

          const bufferView = new Uint8Array(chunk.byteLength);
          chunk.copyTo(bufferView);
          encodedChunks.push({
            chunk: {
              data: bufferView.buffer,
              duration: chunk.duration,
              timestamp: chunk.timestamp,
              type: chunk.type,
              byteLength: chunk.byteLength,
            },
            metadata: chunkMetadata,
          });

          processFrameCount--;

          // 每100帧打印一次进度，避免过多日志
          if (encodedChunks.length % 100 === 0) {
            console.log(`已编码 ${encodedChunks.length}/${validFrameCount} 帧`);
          }
        },
        error: (e) => {
          console.error("编码器错误", e);
          encodeError = e;
          self.postMessage({
            type: "error",
            message: e.message,
          });
        },
      });

      encoder.configure(encoderConfig);
    } catch (error) {
      console.error("prepare webcodec error:", error);
      throw error;
    }
  });
};

let frameQueueLimit = 100; // 控制处理的最大队列数

const startCompress = async () => {
  console.log("start compress");
  await prepareWebCodec(videoTrack, videoDescription);
  if (decodeQueue.length) {
    console.log(
      "解码器队列数量：",
      decodeQueue.length,
      ",处理队列最大长度：",
      frameQueueLimit,
    );
    for (const chunk of decodeQueue) {
      while (processFrameCount > frameQueueLimit) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      decoder.decode(chunk);
      processFrameCount++;
    }
    decodeQueue = [];
  }

  console.log("视频数据已全部推送到解码器...");

  if (decoder.state !== "closed") {
    await decoder.flush();
  }

  if (smartFrameDropper) {
    smartFrameDropper.getStats();
  }

  console.log("解码完成，等待编码完成...");

  if (decodeError) {
    throw decodeError;
  }

  // 等待编码完成
  if (encoder.state !== "closed") {
    await encoder.flush();
  }
  console.log(`编码完成，共生成 ${encodedChunks.length} 个编码块`);

  if (encodeError) {
    throw encodeError;
  }
  return encodedChunks;
};

let smartFrameDropper = null;
let validFrameCount = 0;
// 处理解码后的帧
const processFrame = async (frame, encoder) => {
  if (!smartFrameDropper) {
    smartFrameDropper = new SmartFrameDropper(
      videoTrack.framerate,
      TARGET_FRAMERATE,
    );
  }
  if (smartFrameDropper.shouldEncodeFrame(frame.type === "key")) {
    // 将解码后的帧送入编码器，并提供必要的编码选项
    validFrameCount++;
    try {
      if (encoder.state === "closed") {
        console.warn("编码器已关闭，无法编码帧");
        return;
      }
      encoder.encode(frame, {
        keyFrame: false, // 让编码器自动决定关键帧
      });
    } catch (error) {
      console.error("编码帧时出错:", error);
      // 如果带选项的编码失败，尝试不带选项
      encoder.encode(frame);
    }
  } else {
    processFrameCount--;
  }
};

const safeClose = () => {
  if (decoder && decoder.state !== "closed") {
    decoder.close();
  }
  if (encoder && encoder.state !== "closed") {
    encoder.close();
  }
  self.close();
};

self.onmessage = (e) => {
  const {
    type,
    data,
    samples,
    index,
    frameQueueLimit: _frameQueueLimit,
  } = e.data;
  switch (type) {
    case "videoInfo":
      /**
       * 接收视频元信息
       */
      {
        videoDescription = data.videoDescription;
        videoTrack = data.videoTrack;
        prepareWebCodec(videoTrack, videoDescription).then(
          () => {},
          () => {},
        );
      }
      break;
    case "samples":
      /**
       * 处理分片
       */
      {
        frameQueueLimit = _frameQueueLimit;
        for (const sample of samples) {
          const bufferView = new Uint8Array(sample.data);
          const chunk = new EncodedVideoChunk({
            ...sample,
            data: bufferView,
          });
          decodeQueue.push(chunk);
        }
        startCompress().then(
          (chunks) => {
            self.postMessage(
              {
                type: "compressEnd",
                chunks,
                index,
              },
              chunks.map((r) => r.chunk.data),
            );
          },
          (error) => {
            self.postMessage({
              type: "error",
              message: error.message,
            });
          },
        );
      }
      break;
    case "close":
      /**
       * 关闭 worker
       */
      safeClose();
      break;
    default:
      break;
  }
};

self.onerror = (err) => {
  self.postMessage({
    type: "error",
    message: err.message,
  });
};

/**
 * 通知主线程 ready
 */
self.postMessage({
  type: "init",
});
